import { useMemo, useState } from 'react';
import { Icon } from './Icon';

export type SortDir = 'asc' | 'desc';
export interface SortState<K extends string> { key: K; dir: SortDir; }

/**
 * Hook do sortowania listy przez kliknięcie nagłówka. Trzyma `{key, dir}` w state,
 * cykl: brak/inne pole → desc → asc → desc (klik na ten sam nagłówek toggle-uje kierunek).
 *
 * @param data       lista wejściowa
 * @param defaultKey domyślne pole sortowania (zwykle 'createdAt' z 'desc')
 * @param defaultDir kierunek startowy
 * @param accessor   funkcja zwracająca wartość porównywalną dla danego pola
 *                   (string/number/Date — undefined/null trafia na koniec)
 */
export function useSortable<T, K extends string>(
  data: T[],
  defaultKey: K,
  defaultDir: SortDir,
  accessor: (row: T, key: K) => string | number | Date | null | undefined,
) {
  const [sort, setSort] = useState<SortState<K>>({ key: defaultKey, dir: defaultDir });

  const sorted = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      const va = accessor(a, sort.key);
      const vb = accessor(b, sort.key);
      // null/undefined zawsze na końcu, niezależnie od kierunku
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      // Daty
      if (va instanceof Date && vb instanceof Date) {
        const cmp = va.getTime() - vb.getTime();
        return sort.dir === 'asc' ? cmp : -cmp;
      }
      // Liczby
      if (typeof va === 'number' && typeof vb === 'number') {
        return sort.dir === 'asc' ? va - vb : vb - va;
      }
      // Stringi (locale-aware dla pl)
      const cmp = String(va).localeCompare(String(vb), 'pl');
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [data, sort, accessor]);

  const onHeaderClick = (key: K) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  return { sorted, sort, onHeaderClick };
}

/** Klikalny `<th>` z indykatorem kierunku sortowania. */
export function SortHeader<K extends string>({
  label, sortKey, current, onClick, className = '', align = 'left',
}: {
  label: React.ReactNode;
  sortKey: K;
  current: SortState<K>;
  onClick: (key: K) => void;
  className?: string;
  align?: 'left' | 'right' | 'center';
}) {
  const active = current.key === sortKey;
  const arrow = !active ? '↕' : current.dir === 'asc' ? '↑' : '↓';
  return (
    <th className={className}>
      <button type="button" onClick={() => onClick(sortKey)}
              className={'inline-flex items-center gap-1 select-none cursor-pointer hover:text-water-700 '
                       + (active ? 'text-ink-900 font-semibold' : 'text-ink-500')
                       + (align === 'right' ? ' justify-end w-full' : align === 'center' ? ' justify-center w-full' : '')}>
        <span>{label}</span>
        <span className={'text-[10px] ' + (active ? 'text-water-600' : 'text-ink-300')}>{arrow}</span>
      </button>
    </th>
  );
}

// Wskaźnik nieużywany — `Icon` reexport tylko gdyby ktoś chciał import
export { Icon };
